import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Download, Settings, History, Eye, RefreshCw, 
  AlertTriangle, CheckCircle, Building2, DollarSign, Users,
  Info, FileDown, CalendarRange, Package, X, Plus, Trash2, FileSpreadsheet,
  Upload, FileCheck, Square, CheckSquare, ShieldCheck, LayoutDashboard,
  Clock, XCircle, Check, Send
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

export default function ReporteF8125() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generar');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Configuración
  const [config, setConfig] = useState({
    cuit_informante: '',
    denominacion_informante: '',
    codigo_rubro: '01',
    umbral_saldo: 400000,
    umbral_transacciones: 1400000
  });
  
  // Parámetros del reporte
  const [periodo, setPeriodo] = useState('');
  const [umbralSaldo, setUmbralSaldo] = useState(400000);
  const [umbralTransacciones, setUmbralTransacciones] = useState(1400000);
  const [esRectificativa, setEsRectificativa] = useState(false);
  const [secuencia, setSecuencia] = useState(0);
  const [generarSinMovimientos, setGenerarSinMovimientos] = useState(false);
  
  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Historial
  const [historial, setHistorial] = useState([]);
  const [presMap, setPresMap] = useState({});
  const [presBusy, setPresBusy] = useState('');
  const [taBusy, setTaBusy] = useState(false);
  
  // Generación múltiple
  const [periodosMultiples, setPeriodosMultiples] = useState([]);
  const [previewMultiple, setPreviewMultiple] = useState(null);
  const [loadingMultiple, setLoadingMultiple] = useState(false);
  const [generatingMultiple, setGeneratingMultiple] = useState(false);
  
  // Historial - selección múltiple
  const [selectedReportes, setSelectedReportes] = useState([]);
  const [uploadingPdf, setUploadingPdf] = useState(null);
  
  // Validación
  const [validacion, setValidacion] = useState(null);
  const [validando, setValidando] = useState(false);
  
  // Dashboard
  const [dashboardData, setDashboardData] = useState({ periodos: [], config: {} });
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  useEffect(() => {
    loadConfig();
    loadHistorial();
    loadDashboard();
    
    // Establecer período actual
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    setPeriodo(`${year}${month}`);
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/reporte-f8125/config');
      setConfig(res.data);
      if (res.data.umbral_saldo) setUmbralSaldo(res.data.umbral_saldo);
      if (res.data.umbral_transacciones) setUmbralTransacciones(res.data.umbral_transacciones);
    } catch (error) {
      console.error('Error cargando configuración:', error);
    }
  };

  const loadHistorial = async () => {
    try {
      const res = await api.get('/reporte-f8125/historial');
      setHistorial(res.data);
      loadPresentaciones();
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  };

  const loadPresentaciones = async () => {
    try {
      const res = await api.get('/arca-presentaciones?tipo=8125');
      const map = {};
      (res.data.rows || []).forEach((p) => { if (!map[p.reporte_id]) map[p.reporte_id] = p; });
      setPresMap(map);
    } catch (e) { /* silencioso */ }
  };

  const presentarArca = async (reporte) => {
    setPresBusy(reporte.id);
    try {
      const res = await api.post('/arca-presentaciones/presentar', { tipo: '8125', reporte_id: reporte.id });
      if (res.data.ok === false) {
        toast.error(res.data.error || 'No se pudo presentar en ARCA');
      } else {
        toast.success(`Presentado en ARCA. Nº transacción: ${res.data.numeroTransaccion || 's/d'}`);
      }
      loadPresentaciones();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'No se pudo presentar en ARCA');
      loadPresentaciones();
    } finally { setPresBusy(''); }
  };

  const consultarArca = async (presId) => {
    setPresBusy(presId);
    try {
      const res = await api.post('/arca-presentaciones/consultar', { presentacion_id: presId });
      if (res.data.ok === false) {
        toast.error(res.data.error || 'No se pudo consultar el estado');
      } else {
        const est = res.data.estado;
        toast.success(est === 'PRESENTADA' ? `Presentada (Nº ${res.data.numeroTransaccion})`
          : est === 'PENDIENTE' ? 'Aún en proceso en ARCA' : 'No encontrada en ARCA');
      }
      loadPresentaciones();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'No se pudo consultar el estado');
    } finally { setPresBusy(''); }
  };

  const forzarTA = async () => {
    setTaBusy(true);
    try {
      const res = await api.post('/arca-presentaciones/forzar-ta');
      toast.success(res.data?.mensaje || 'Token WSAA renovado. Ya podés reintentar la presentación.');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'No se pudo renovar el token');
    } finally { setTaBusy(false); }
  };

  const loadDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const res = await api.get('/cierre-periodo/dashboard');
      setDashboardData(res.data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const handleMarcarCierre = async (periodo, tipo, completo) => {
    try {
      await api.post('/cierre-periodo/marcar', {
        periodo,
        tipo,
        completo,
        comentarios: completo ? 'Marcado como completo' : 'Reabierto'
      });
      toast.success(`${tipo} ${completo ? 'marcado como completo' : 'reabierto'}`);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleMarcarPresentado = async (reporteId) => {
    const numero = prompt('Número de presentación AFIP (opcional):');
    try {
      await api.post('/cierre-periodo/marcar-presentado', {
        reporte_id: reporteId,
        numero_presentacion: numero || null
      });
      toast.success('Reporte marcado como presentado');
      loadDashboard();
      loadHistorial();
    } catch (error) {
      toast.error('Error al marcar como presentado');
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await api.put('/reporte-f8125/config', config);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!periodo) {
      toast.error('Seleccione un período');
      return;
    }
    
    setLoadingPreview(true);
    try {
      const res = await api.post('/reporte-f8125/preview', {
        periodo,
        umbral_saldo: umbralSaldo,
        umbral_transacciones: umbralTransacciones
      });
      setPreviewData(res.data);
      
      // Actualizar secuencia sugerida
      if (!esRectificativa) {
        setSecuencia(0);
      } else if (res.data.proxima_secuencia) {
        setSecuencia(res.data.proxima_secuencia);
      }
      
      toast.success(`Vista previa generada: ${res.data.total_clientes} clientes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar vista previa');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGenerar = async () => {
    if (!config.cuit_informante || !config.denominacion_informante) {
      toast.error('Configure los datos del informante primero');
      setActiveTab('config');
      return;
    }
    
    // Permitir generar si hay datos O si está habilitado generarSinMovimientos
    if (!generarSinMovimientos && (!previewData || previewData.clientes.length === 0)) {
      toast.error('Primero genere una vista previa con datos o habilite "Generar sin movimientos"');
      return;
    }
    
    if (esRectificativa && secuencia < 1) {
      toast.error('La secuencia para rectificativa debe ser >= 1');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await api.post('/reporte-f8125/generar', {
        periodo,
        umbral_saldo: umbralSaldo,
        umbral_transacciones: umbralTransacciones,
        es_rectificativa: esRectificativa,
        secuencia: esRectificativa ? secuencia : 0,
        generar_sin_movimientos: generarSinMovimientos
      }, { responseType: 'blob' });
      
      // Descargar archivo
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const sec = esRectificativa ? secuencia : 0;
      a.download = `F8125.${config.cuit_informante.replace(/-/g, '')}.${periodo}00.${String(sec).padStart(4, '0')}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Archivo F8125 generado y almacenado exitosamente');
      loadHistorial();
    } catch (error) {
      toast.error('Error al generar archivo');
    } finally {
      setGenerating(false);
    }
  };

  const handleDescargarHistorial = async (reporte) => {
    try {
      const response = await api.get(`/reporte-f8125/descargar/${reporte.id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = reporte.nombre_archivo || `F8125_${reporte.id}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Archivo descargado');
    } catch (error) {
      toast.error('Error al descargar archivo');
    }
  };

  const handleEliminarReporte = async (reporte) => {
    if (!window.confirm(`¿Eliminar el reporte ${reporte.nombre_archivo}?`)) {
      return;
    }
    
    try {
      await api.delete(`/reporte-f8125/eliminar/${reporte.id}`);
      toast.success('Reporte eliminado');
      loadHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar reporte');
    }
  };

  // Funciones para selección múltiple
  const toggleSelectReporte = (id) => {
    setSelectedReportes(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedReportes.length === historial.length) {
      setSelectedReportes([]);
    } else {
      setSelectedReportes(historial.map(r => r.id));
    }
  };

  const handleEliminarMultiple = async () => {
    if (selectedReportes.length === 0) {
      toast.error('Seleccione al menos un reporte');
      return;
    }
    
    if (!window.confirm(`¿Eliminar ${selectedReportes.length} reportes?`)) {
      return;
    }
    
    try {
      await api.post('/reporte-f8125/eliminar-multiple', { ids: selectedReportes });
      toast.success(`${selectedReportes.length} reportes eliminados`);
      setSelectedReportes([]);
      loadHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar reportes');
    }
  };

  const handleDescargarMultiple = async () => {
    if (selectedReportes.length === 0) {
      toast.error('Seleccione al menos un reporte');
      return;
    }
    
    try {
      const response = await api.post('/reporte-f8125/descargar-multiple', 
        { ids: selectedReportes },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `F8125_Historial_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${selectedReportes.length} reportes descargados`);
    } catch (error) {
      toast.error('Error al descargar reportes');
    }
  };

  // Funciones para PDF de aceptación
  const handleSubirAceptacion = async (reporte, file) => {
    if (!file) return;
    
    setUploadingPdf(reporte.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/reporte-f8125/aceptacion/${reporte.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('PDF de aceptación cargado');
      loadHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar PDF');
    } finally {
      setUploadingPdf(null);
    }
  };

  const handleDescargarAceptacion = async (reporte) => {
    try {
      const response = await api.get(`/reporte-f8125/aceptacion/${reporte.id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = reporte.aceptacion_nombre || `Aceptacion_${reporte.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  const handleEliminarAceptacion = async (reporte) => {
    if (!window.confirm('¿Eliminar el PDF de aceptación?')) return;
    
    try {
      await api.delete(`/reporte-f8125/aceptacion/${reporte.id}`);
      toast.success('PDF eliminado');
      loadHistorial();
    } catch (error) {
      toast.error('Error al eliminar PDF');
    }
  };

  const handleDescargarResumenHistorial = async (reporte) => {
    try {
      const response = await api.get(`/reporte-f8125/resumen-historial/${reporte.id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Resumen_${reporte.periodo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Resumen descargado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al descargar resumen');
    }
  };

  const handleDescargarResumen = async () => {
    if (!previewData || previewData.clientes.length === 0) {
      toast.error('Primero genere una vista previa con datos');
      return;
    }
    
    try {
      const response = await api.post('/reporte-f8125/resumen-excel', {
        periodo,
        umbral_saldo: umbralSaldo,
        umbral_transacciones: umbralTransacciones
      }, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Resumen_F8125_${periodo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Resumen descargado');
    } catch (error) {
      toast.error('Error al descargar resumen');
    }
  };

  const handleValidar = async () => {
    if (!periodo || periodo.length !== 6) {
      toast.error('Ingrese un período válido');
      return;
    }
    
    setValidando(true);
    try {
      const res = await api.post('/reporte-f8125/validar', {
        periodo,
        umbral_saldo: umbralSaldo,
        umbral_transacciones: umbralTransacciones
      });
      setValidacion(res.data);
      
      if (res.data.valido) {
        toast.success('Validación exitosa - El archivo puede generarse');
      } else {
        toast.error('Hay errores que deben corregirse');
      }
    } catch (error) {
      toast.error('Error al validar');
    } finally {
      setValidando(false);
    }
  };

  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(monto);
  };

  const formatPeriodo = (periodo) => {
    if (!periodo || periodo.length !== 6) return periodo;
    const año = periodo.substring(0, 4);
    const mes = periodo.substring(4, 6);
    const meses = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${meses[parseInt(mes)]} ${año}`;
  };

  // Funciones para generación múltiple
  const addPeriodo = () => {
    if (periodosMultiples.length >= 12) {
      toast.error('Máximo 12 períodos por generación');
      return;
    }
    // Calcular siguiente período basado en el último añadido o el actual
    let nuevoPeriodo = '';
    if (periodosMultiples.length > 0) {
      const ultimo = periodosMultiples[periodosMultiples.length - 1];
      const año = parseInt(ultimo.substring(0, 4));
      const mes = parseInt(ultimo.substring(4, 6));
      if (mes === 12) {
        nuevoPeriodo = `${año + 1}01`;
      } else {
        nuevoPeriodo = `${año}${String(mes + 1).padStart(2, '0')}`;
      }
    } else {
      nuevoPeriodo = periodo;
    }
    setPeriodosMultiples([...periodosMultiples, nuevoPeriodo]);
  };

  const removePeriodo = (index) => {
    setPeriodosMultiples(periodosMultiples.filter((_, i) => i !== index));
    setPreviewMultiple(null);
  };

  const updatePeriodo = (index, value) => {
    const nuevos = [...periodosMultiples];
    nuevos[index] = value;
    setPeriodosMultiples(nuevos);
  };

  const handlePreviewMultiple = async () => {
    if (periodosMultiples.length === 0) {
      toast.error('Agregue al menos un período');
      return;
    }
    
    setLoadingMultiple(true);
    try {
      const res = await api.post('/reporte-f8125/preview-multiple', {
        periodos: periodosMultiples,
        umbral_saldo: umbralSaldo,
        umbral_transacciones: umbralTransacciones
      });
      setPreviewMultiple(res.data);
      toast.success(`Vista previa generada para ${res.data.resumen.periodos_con_datos} períodos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar vista previa');
    } finally {
      setLoadingMultiple(false);
    }
  };

  const handleGenerarMultiple = async () => {
    if (!config.cuit_informante || !config.denominacion_informante) {
      toast.error('Configure los datos del informante primero');
      setActiveTab('config');
      return;
    }
    
    // Permitir generar si hay datos O si está habilitado generarSinMovimientos
    if (!generarSinMovimientos && (!previewMultiple || previewMultiple.resumen.periodos_con_datos === 0)) {
      toast.error('Primero genere una vista previa con datos o habilite "Generar sin movimientos"');
      return;
    }
    
    setGeneratingMultiple(true);
    try {
      const response = await api.post('/reporte-f8125/generar-multiple', {
        periodos: periodosMultiples,
        umbral_saldo: umbralSaldo,
        umbral_transacciones: umbralTransacciones,
        es_rectificativa: esRectificativa,
        secuencia: esRectificativa ? secuencia : 0,
        generar_sin_movimientos: generarSinMovimientos
      }, { responseType: 'blob' });
      
      // Descargar archivo ZIP
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fecha = new Date().toISOString().split('T')[0].replace(/-/g, '');
      a.download = `F8125_Multiple_${fecha}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Archivos F8125 generados y almacenados exitosamente');
      loadHistorial();
    } catch (error) {
      toast.error('Error al generar archivos');
    } finally {
      setGeneratingMultiple(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reporte F8125 AFIP</h1>
          <p className="text-slate-500">Régimen de información de acreditaciones sobre cuentas</p>
        </div>
        <Badge className="bg-blue-100 text-blue-800">
          <FileText size={14} className="mr-1" />
          Formulario 8125
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="generar" className="flex items-center gap-2">
            <Download size={16} />
            Generar Reporte
          </TabsTrigger>
          <TabsTrigger value="multiple" className="flex items-center gap-2">
            <CalendarRange size={16} />
            Múltiples Períodos
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings size={16} />
            Configuración
          </TabsTrigger>
          <TabsTrigger value="historial" className="flex items-center gap-2">
            <History size={16} />
            Historial
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <LayoutDashboard size={16} />
            Dashboard
          </TabsTrigger>
        </TabsList>

        {/* Tab: Generar Reporte */}
        <TabsContent value="generar" className="space-y-6">
          {/* Parámetros */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Parámetros del Reporte</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Período (AAAAMM)</Label>
                <Input
                  value={periodo}
                  onChange={(e) => setPeriodo(e.target.value)}
                  placeholder="202501"
                  maxLength={6}
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">{formatPeriodo(periodo)}</p>
              </div>
              
              <div>
                <Label>Umbral Saldo Final ($)</Label>
                <Input
                  type="number"
                  value={umbralSaldo}
                  onChange={(e) => setUmbralSaldo(parseFloat(e.target.value) || 0)}
                  placeholder="400000"
                />
                <p className="text-xs text-slate-500 mt-1">Saldo &gt; {formatMonto(umbralSaldo)}</p>
              </div>
              
              <div>
                <Label>Umbral Transacciones ($)</Label>
                <Input
                  type="number"
                  value={umbralTransacciones}
                  onChange={(e) => setUmbralTransacciones(parseFloat(e.target.value) || 0)}
                  placeholder="1400000"
                />
                <p className="text-xs text-slate-500 mt-1">Suma &gt; {formatMonto(umbralTransacciones)}</p>
              </div>
              
              <div className="flex items-end">
                <Button
                  onClick={handlePreview}
                  disabled={loadingPreview}
                  className="w-full"
                  style={{ backgroundColor: '#2563eb', color: 'white' }}
                >
                  {loadingPreview ? (
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Eye size={16} className="mr-2" />
                  )}
                  Vista Previa
                </Button>
              </div>
            </div>
            
            {/* Info de criterios */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Criterios de inclusión (se reporta si cumple CUALQUIERA):</p>
                  <ul className="list-disc list-inside mt-1 text-blue-700">
                    <li>Saldo al final del mes mayor a {formatMonto(umbralSaldo)}</li>
                    <li>Suma de transacciones (ingresos + egresos) mayor a {formatMonto(umbralTransacciones)}</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Opción para generar sin movimientos - siempre visible */}
            <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sinMovimientosDirecto"
                    checked={generarSinMovimientos}
                    onChange={(e) => setGenerarSinMovimientos(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <div>
                    <Label htmlFor="sinMovimientosDirecto" className="text-sm font-medium cursor-pointer text-amber-800">
                      Generar archivo sin movimientos (solo cabecera)
                    </Label>
                    <p className="text-xs text-amber-600">Use esta opción si no hay transacciones que reportar en el período</p>
                  </div>
                </div>
                {generarSinMovimientos && (
                  <Button
                    onClick={handleGenerar}
                    disabled={generating || !periodo || !config.cuit_informante}
                    style={{ backgroundColor: '#d97706', color: 'white' }}
                  >
                    {generating ? (
                      <RefreshCw size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Download size={16} className="mr-2" />
                    )}
                    Generar F8125 Solo Cabecera
                  </Button>
                )}
              </div>
            </div>
            
            {/* Botón de Validación */}
            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleValidar}
                disabled={validando || !periodo}
                variant="outline"
              >
                {validando ? (
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                ) : (
                  <ShieldCheck size={16} className="mr-2" />
                )}
                Validar Formato AFIP
              </Button>
            </div>
            
            {/* Resultado de validación */}
            {validacion && (
              <div className={`mt-4 p-4 rounded-lg border ${
                validacion.valido 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {validacion.valido ? (
                    <>
                      <CheckCircle className="text-green-600" size={20} />
                      <span className="font-medium text-green-800">Validación exitosa</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="text-red-600" size={20} />
                      <span className="font-medium text-red-800">Se encontraron errores</span>
                    </>
                  )}
                </div>
                
                {validacion.errores?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-red-700">Errores:</p>
                    <ul className="list-disc list-inside text-sm text-red-600">
                      {validacion.errores.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                )}
                
                {validacion.advertencias?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-amber-700">Advertencias:</p>
                    <ul className="list-disc list-inside text-sm text-amber-600">
                      {validacion.advertencias.map((a) => <li key={a}>{a}</li>)}
                    </ul>
                  </div>
                )}
                
                {validacion.estadisticas && (
                  <div className="text-sm text-slate-600 flex gap-4 mt-2">
                    <span>Registros: {validacion.estadisticas.total_registros}</span>
                    <span>Vendedores: {validacion.estadisticas.total_vendedores}</span>
                    <span>Detalles: {validacion.estadisticas.total_detalles}</span>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Tipo de presentación - Siempre visible */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Tipo de Presentación</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <Label className="text-slate-900">¿Es Rectificativa?</Label>
                  <p className="text-xs text-slate-500">Marcar si está corrigiendo una presentación anterior</p>
                </div>
                <Switch
                  checked={esRectificativa}
                  onCheckedChange={(checked) => {
                    setEsRectificativa(checked);
                    if (!checked) setSecuencia(0);
                    else setSecuencia(1);
                  }}
                />
              </div>
              
              {esRectificativa && (
                <div>
                  <Label>Número de Secuencia</Label>
                  <Input
                    type="number"
                    value={secuencia}
                    onChange={(e) => setSecuencia(parseInt(e.target.value) || 1)}
                    min={1}
                    className="font-mono"
                  />
                </div>
              )}
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800">
                  {esRectificativa ? (
                    <>Rectificativa #{secuencia}</>
                  ) : (
                    <>Presentación Original (Secuencia 00)</>
                  )}
                </p>
              </div>
            </div>
          </Card>

          {/* Preview Results */}
          {previewData && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Users size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-900">{previewData.total_clientes}</p>
                      <p className="text-xs text-blue-600">Clientes a Reportar</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <DollarSign size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-900">
                        {formatMonto(previewData.monto_total_transacciones)}
                      </p>
                      <p className="text-xs text-green-600">Total Transacciones</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500 rounded-lg">
                      <Building2 size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-900">{formatPeriodo(previewData.periodo)}</p>
                      <p className="text-xs text-amber-600">Período</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500 rounded-lg">
                      <DollarSign size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-purple-900">
                        {formatMonto(previewData.monto_total_saldo)}
                      </p>
                      <p className="text-xs text-purple-600">Saldo Neto Total</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Table */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">
                    Clientes a Incluir en el Reporte
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleDescargarResumen}
                      disabled={previewData.clientes.length === 0}
                      variant="outline"
                    >
                      <FileSpreadsheet size={16} className="mr-2" />
                      Descargar Resumen
                    </Button>
                    <Button
                      onClick={handleGenerar}
                      disabled={generating || previewData.clientes.length === 0}
                      style={{ backgroundColor: '#059669', color: 'white' }}
                    >
                      {generating ? (
                        <RefreshCw size={16} className="mr-2 animate-spin" />
                      ) : (
                        <Download size={16} className="mr-2" />
                      )}
                      Generar y Guardar F8125
                    </Button>
                  </div>
                </div>

                {previewData.clientes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <AlertTriangle size={40} className="mx-auto mb-2 text-amber-400" />
                    <p>No hay clientes que cumplan los criterios de inclusión</p>
                    <p className="text-sm mb-4">Intente ajustar los umbrales o cambiar el período</p>
                    
                    {/* Opción para generar sin movimientos */}
                    <div className="inline-flex flex-col items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="sinMovimientosF8125"
                          checked={generarSinMovimientos}
                          onChange={(e) => setGenerarSinMovimientos(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <Label htmlFor="sinMovimientosF8125" className="text-sm cursor-pointer text-amber-800">
                          Generar archivo sin movimientos (solo cabecera)
                        </Label>
                      </div>
                      {generarSinMovimientos && (
                        <Button
                          onClick={handleGenerar}
                          disabled={generating}
                          style={{ backgroundColor: '#d97706', color: 'white' }}
                          size="sm"
                        >
                          {generating ? (
                            <RefreshCw size={16} className="mr-2 animate-spin" />
                          ) : (
                            <Download size={16} className="mr-2" />
                          )}
                          Generar F8125 Solo Cabecera
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="max-h-[400px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>CUIT</TableHead>
                          <TableHead>Razón Social</TableHead>
                          <TableHead className="text-right">Ingresos</TableHead>
                          <TableHead className="text-right">Egresos</TableHead>
                          <TableHead className="text-right">Suma Trans.</TableHead>
                          <TableHead className="text-right">Saldo Final</TableHead>
                          <TableHead>Motivo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.clientes.map((cliente, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono">{cliente.cuit}</TableCell>
                            <TableCell className="max-w-[150px] truncate">
                              {cliente.razon_social || '-'}
                            </TableCell>
                            <TableCell className="text-right text-green-600">
                              {formatMonto(cliente.monto_ingresos)}
                            </TableCell>
                            <TableCell className="text-right text-red-600">
                              {formatMonto(cliente.monto_egresos)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatMonto(cliente.suma_transacciones)}
                            </TableCell>
                            <TableCell className={`text-right font-semibold ${cliente.saldo_final >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatMonto(cliente.saldo_final)}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {cliente.motivo_inclusion?.map((m) => (
                                  <Badge key={m} className="bg-slate-100 text-slate-700 text-xs whitespace-nowrap">
                                    {m.includes('Saldo') ? '💰' : '📊'} {m.split(' >= ')[0]}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {/* Tab: Múltiples Períodos */}
        <TabsContent value="multiple" className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Generación de Múltiples Períodos</h2>
                <p className="text-sm text-slate-500">Genere varios reportes F8125 en un solo archivo ZIP</p>
              </div>
              <Button
                onClick={addPeriodo}
                variant="outline"
                className="flex items-center gap-2"
                disabled={periodosMultiples.length >= 12}
              >
                <Plus size={16} />
                Agregar Período
              </Button>
            </div>

            {/* Lista de períodos */}
            {periodosMultiples.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                <CalendarRange size={40} className="mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500">No hay períodos seleccionados</p>
                <p className="text-sm text-slate-400">Haga clic en "Agregar Período" para comenzar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {periodosMultiples.map((p, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-500 w-6">{idx + 1}.</span>
                    <Input
                      value={p}
                      onChange={(e) => updatePeriodo(idx, e.target.value)}
                      placeholder="AAAAMM"
                      maxLength={6}
                      className="font-mono w-32"
                    />
                    <span className="text-sm text-slate-500 flex-1">{formatPeriodo(p)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removePeriodo(idx)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Umbrales */}
            {periodosMultiples.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Umbrales (aplicados a todos los períodos)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Umbral Saldo Final ($)</Label>
                    <Input
                      type="number"
                      value={umbralSaldo}
                      onChange={(e) => setUmbralSaldo(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Umbral Transacciones ($)</Label>
                    <Input
                      type="number"
                      value={umbralTransacciones}
                      onChange={(e) => setUmbralTransacciones(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                
                {/* Opción de Rectificativa */}
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <Label className="text-slate-900">¿Es Rectificativa?</Label>
                        <p className="text-xs text-slate-500">Todos los archivos tendrán la misma secuencia</p>
                      </div>
                      <Switch
                        checked={esRectificativa}
                        onCheckedChange={(checked) => {
                          setEsRectificativa(checked);
                          if (checked && secuencia < 1) setSecuencia(1);
                        }}
                      />
                    </div>
                    
                    {esRectificativa && (
                      <div>
                        <Label>Número de Secuencia</Label>
                        <Input
                          type="number"
                          value={secuencia}
                          onChange={(e) => setSecuencia(parseInt(e.target.value) || 1)}
                          min={1}
                          className="font-mono"
                        />
                      </div>
                    )}
                    
                    <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm font-medium text-amber-800">
                        {esRectificativa ? (
                          <>Rectificativa #{String(secuencia).padStart(2, '0')}</>
                        ) : (
                          <>Presentación Original (Sec. 00)</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Opción de generar sin movimientos para múltiples períodos */}
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="sinMovimientosMultiple"
                        checked={generarSinMovimientos}
                        onChange={(e) => setGenerarSinMovimientos(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <div>
                        <Label htmlFor="sinMovimientosMultiple" className="text-sm font-medium cursor-pointer text-amber-800">
                          Generar archivos sin movimientos (solo cabecera)
                        </Label>
                        <p className="text-xs text-amber-600">Genera archivos vacíos para todos los períodos seleccionados</p>
                      </div>
                    </div>
                    {generarSinMovimientos && (
                      <Button
                        onClick={handleGenerarMultiple}
                        disabled={generatingMultiple || periodosMultiples.length === 0}
                        style={{ backgroundColor: '#d97706', color: 'white' }}
                      >
                        {generatingMultiple ? (
                          <RefreshCw size={16} className="mr-2 animate-spin" />
                        ) : (
                          <Package size={16} className="mr-2" />
                        )}
                        Generar ZIP Solo Cabeceras
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 flex gap-3">
                  <Button
                    onClick={handlePreviewMultiple}
                    disabled={loadingMultiple}
                    variant="outline"
                    className="flex-1"
                  >
                    {loadingMultiple ? (
                      <RefreshCw size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Eye size={16} className="mr-2" />
                    )}
                    Vista Previa
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Preview múltiple */}
          {previewMultiple && (
            <>
              {/* Resumen */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <CalendarRange size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-900">{previewMultiple.resumen.total_periodos}</p>
                      <p className="text-xs text-blue-600">Períodos Total</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <CheckCircle size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-900">{previewMultiple.resumen.periodos_con_datos}</p>
                      <p className="text-xs text-green-600">Con Datos</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500 rounded-lg">
                      <Users size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-900">{previewMultiple.resumen.total_clientes_global}</p>
                      <p className="text-xs text-purple-600">Clientes Total</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500 rounded-lg">
                      <DollarSign size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-900">
                        {formatMonto(previewMultiple.resumen.total_transacciones_global)}
                      </p>
                      <p className="text-xs text-amber-600">Trans. Total</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Tabla de períodos */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">Detalle por Período</h2>
                  <div className="flex items-center gap-4">
                    {/* Checkbox para generar sin movimientos */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="sinMovimientosMultipleF8125"
                        checked={generarSinMovimientos}
                        onChange={(e) => setGenerarSinMovimientos(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <Label htmlFor="sinMovimientosMultipleF8125" className="text-sm cursor-pointer">
                        Generar sin movimientos
                      </Label>
                    </div>
                    <Button
                      onClick={handleGenerarMultiple}
                      disabled={generatingMultiple || (!generarSinMovimientos && previewMultiple.resumen.periodos_con_datos === 0)}
                      style={{ backgroundColor: '#059669', color: 'white' }}
                    >
                      {generatingMultiple ? (
                        <RefreshCw size={16} className="mr-2 animate-spin" />
                      ) : (
                        <Package size={16} className="mr-2" />
                      )}
                      {generarSinMovimientos && previewMultiple.resumen.periodos_con_datos === 0 
                        ? `Generar ZIP (Solo Cabeceras)`
                        : `Generar ZIP con ${previewMultiple.resumen.periodos_con_datos} Archivos`
                      }
                    </Button>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Período</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Clientes</TableHead>
                      <TableHead className="text-right">Total Transacciones</TableHead>
                      <TableHead className="text-right">Saldo Neto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewMultiple.periodos.map((p, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono">{formatPeriodo(p.periodo)}</TableCell>
                        <TableCell>
                          {p.status === 'ok' && p.total_clientes > 0 ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle size={12} className="mr-1" />
                              OK
                            </Badge>
                          ) : p.status === 'ok' ? (
                            <Badge className="bg-amber-100 text-amber-800">
                              <AlertTriangle size={12} className="mr-1" />
                              Sin datos
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <AlertTriangle size={12} className="mr-1" />
                              Error
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{p.total_clientes || 0}</TableCell>
                        <TableCell className="text-right">
                          {formatMonto(p.monto_total_transacciones || 0)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatMonto(p.monto_total_saldo || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Tab: Configuración */}
        <TabsContent value="config">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Datos del Informante
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Configure los datos de su empresa para el encabezado del archivo F8125
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>CUIT del Informante *</Label>
                <Input
                  value={config.cuit_informante}
                  onChange={(e) => setConfig({ ...config, cuit_informante: e.target.value })}
                  placeholder="30-12345678-9"
                  className="font-mono"
                />
              </div>
              
              <div>
                <Label>Código de Rubro</Label>
                <Input
                  value={config.codigo_rubro}
                  onChange={(e) => setConfig({ ...config, codigo_rubro: e.target.value })}
                  placeholder="01"
                  maxLength={2}
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">Según tabla de rubros AFIP</p>
              </div>
              
              <div className="md:col-span-2">
                <Label>Denominación del Informante *</Label>
                <Input
                  value={config.denominacion_informante}
                  onChange={(e) => setConfig({ ...config, denominacion_informante: e.target.value })}
                  placeholder="RAZÓN SOCIAL DE LA EMPRESA"
                  maxLength={200}
                />
                <p className="text-xs text-slate-500 mt-1">Máximo 200 caracteres</p>
              </div>
              
              <div>
                <Label>Umbral Saldo por Defecto ($)</Label>
                <Input
                  type="number"
                  value={config.umbral_saldo}
                  onChange={(e) => setConfig({ ...config, umbral_saldo: parseFloat(e.target.value) || 0 })}
                  placeholder="400000"
                />
                <p className="text-xs text-slate-500 mt-1">Saldo final mayor a este monto</p>
              </div>
              
              <div>
                <Label>Umbral Transacciones por Defecto ($)</Label>
                <Input
                  type="number"
                  value={config.umbral_transacciones}
                  onChange={(e) => setConfig({ ...config, umbral_transacciones: parseFloat(e.target.value) || 0 })}
                  placeholder="1400000"
                />
                <p className="text-xs text-slate-500 mt-1">Suma de transacciones mayor a este monto</p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleSaveConfig}
                disabled={loading}
                style={{ backgroundColor: '#2563eb', color: 'white' }}
              >
                {loading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <CheckCircle size={16} className="mr-2" />}
                Guardar Configuración
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Historial */}
        <TabsContent value="historial">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Historial de Reportes Generados
              </h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={forzarTA}
                  disabled={taBusy}
                  data-testid="f8125-forzar-ta-btn"
                  title="Renueva el token de ARCA (úsalo tras cambiar delegaciones/autorizaciones en ARCA, antes de reintentar)"
                >
                  <RefreshCw size={14} className={`mr-1 ${taBusy ? 'animate-spin' : ''}`} />
                  {taBusy ? 'Renovando…' : 'Forzar token ARCA'}
                </Button>
                {selectedReportes.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDescargarMultiple}
                    >
                      <Download size={14} className="mr-1" />
                      Descargar ({selectedReportes.length})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEliminarMultiple}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Eliminar ({selectedReportes.length})
                    </Button>
                  </>
                )}
              </div>
            </div>

            {historial.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <History size={40} className="mx-auto mb-2 text-slate-300" />
                <p>No hay reportes generados aún</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <button 
                        onClick={toggleSelectAll}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        {selectedReportes.length === historial.length ? (
                          <CheckSquare size={18} className="text-blue-600" />
                        ) : (
                          <Square size={18} className="text-slate-400" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Clientes</TableHead>
                    <TableHead>Total Trans.</TableHead>
                    <TableHead>Aceptación</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historial.map((reporte, idx) => (
                    <TableRow key={idx} className={selectedReportes.includes(reporte.id) ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <button 
                          onClick={() => toggleSelectReporte(reporte.id)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {selectedReportes.includes(reporte.id) ? (
                            <CheckSquare size={18} className="text-blue-600" />
                          ) : (
                            <Square size={18} className="text-slate-400" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm">
                        {reporte.generado_at ? new Date(reporte.generado_at).toLocaleString('es-AR') : '-'}
                      </TableCell>
                      <TableCell className="font-mono">{formatPeriodo(reporte.periodo)}</TableCell>
                      <TableCell>
                        <Badge className={reporte.es_rectificativa ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                          {reporte.es_rectificativa ? `Rectif. #${reporte.secuencia}` : 'Original'}
                        </Badge>
                      </TableCell>
                      <TableCell>{reporte.total_clientes}</TableCell>
                      <TableCell>{formatMonto(reporte.monto_total_transacciones || 0)}</TableCell>
                      <TableCell>
                        {reporte.aceptacion_nombre ? (
                          <div className="flex items-center gap-1">
                            <Badge className="bg-green-100 text-green-800 cursor-pointer" onClick={() => handleDescargarAceptacion(reporte)}>
                              <FileCheck size={12} className="mr-1" />
                              {reporte.aceptacion_nombre.substring(0, 15)}...
                            </Badge>
                            <button
                              onClick={() => handleEliminarAceptacion(reporte)}
                              className="text-red-400 hover:text-red-600 p-1"
                              title="Eliminar PDF"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              onChange={(e) => handleSubirAceptacion(reporte, e.target.files?.[0])}
                              disabled={uploadingPdf === reporte.id}
                            />
                            <span className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800">
                              {uploadingPdf === reporte.id ? (
                                <RefreshCw size={12} className="mr-1 animate-spin" />
                              ) : (
                                <Upload size={12} className="mr-1" />
                              )}
                              Cargar PDF
                            </span>
                          </label>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 items-center">
                          {(() => {
                            const p = presMap[reporte.id];
                            const busy = presBusy === reporte.id || (p && presBusy === p.id);
                            if (p && p.estado === 'PRESENTADA') {
                              return (
                                <>
                                  <Badge className="bg-green-100 text-green-700 border-0 text-[10px]" title="Presentada en ARCA">
                                    <CheckCircle size={11} className="mr-1" />Nº {p.numeroTransaccion || 'OK'}
                                  </Badge>
                                  <Button variant="ghost" size="sm" className="h-8" title="Consultar estado en ARCA"
                                    onClick={() => consultarArca(p.id)} disabled={busy}
                                    data-testid={`f8125-arca-estado-${reporte.id}`}>
                                    <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />
                                  </Button>
                                </>
                              );
                            }
                            return (
                              <>
                                {p && p.estado === 'ERROR' && (
                                  <Badge className="bg-red-100 text-red-700 border-0 text-[10px]" title={p.error || 'Error'}>Error</Badge>
                                )}
                                <Button variant="outline" size="sm" className="h-8 text-teal-700 border-teal-300"
                                  onClick={() => presentarArca(reporte)} disabled={busy}
                                  title="Presentar esta DDJJ en ARCA"
                                  data-testid={`f8125-arca-presentar-${reporte.id}`}>
                                  {busy ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                                  {p && p.estado === 'ERROR' ? 'Reintentar' : 'Presentar en ARCA'}
                                </Button>
                              </>
                            );
                          })()}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDescargarHistorial(reporte)}
                            className="h-8"
                            title="Descargar archivo TXT"
                          >
                            <FileDown size={14} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDescargarResumenHistorial(reporte)}
                            className="h-8"
                            title="Descargar resumen Excel"
                          >
                            <FileSpreadsheet size={14} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEliminarReporte(reporte)}
                            className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Tab: Dashboard */}
        <TabsContent value="dashboard">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Estado de Presentaciones</h2>
              <Button variant="outline" size="sm" onClick={loadDashboard} disabled={loadingDashboard}>
                <RefreshCw size={14} className={`mr-1 ${loadingDashboard ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
            
            {loadingDashboard ? (
              <div className="text-center py-8 text-slate-500">
                <RefreshCw className="animate-spin mx-auto mb-2" />
                Cargando...
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead>Vencimiento</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-center">Movimientos</TableHead>
                    <TableHead className="text-center">Padrón</TableHead>
                    <TableHead className="text-center">Reportes</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.periodos?.map((p, idx) => (
                    <TableRow key={idx} className={p.estado === 'vencido' ? 'bg-red-50' : p.estado === 'por_vencer' ? 'bg-amber-50' : ''}>
                      <TableCell className="font-medium">{formatPeriodo(p.periodo)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(p.fecha_vencimiento).toLocaleDateString('es-AR')}
                          <div className={`text-xs ${p.dias_para_vencer < 0 ? 'text-red-600' : p.dias_para_vencer <= 5 ? 'text-amber-600' : 'text-slate-500'}`}>
                            {p.dias_para_vencer < 0 ? `Vencido hace ${Math.abs(p.dias_para_vencer)} días` : `En ${p.dias_para_vencer} días`}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          p.estado === 'presentado' ? 'bg-green-100 text-green-800' :
                          p.estado === 'generado' ? 'bg-blue-100 text-blue-800' :
                          p.estado === 'listo' ? 'bg-emerald-100 text-emerald-800' :
                          p.estado === 'por_vencer' ? 'bg-amber-100 text-amber-800' :
                          p.estado === 'vencido' ? 'bg-red-100 text-red-800' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {p.estado === 'presentado' && <CheckCircle size={12} className="mr-1" />}
                          {p.estado === 'generado' && <FileText size={12} className="mr-1" />}
                          {p.estado === 'listo' && <Check size={12} className="mr-1" />}
                          {p.estado === 'por_vencer' && <AlertTriangle size={12} className="mr-1" />}
                          {p.estado === 'vencido' && <XCircle size={12} className="mr-1" />}
                          {p.estado === 'pendiente' && <Clock size={12} className="mr-1" />}
                          {p.estado?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleMarcarCierre(p.periodo, 'movimientos', !p.cierre?.movimientos_completo)}
                          className={`p-2 rounded-full transition-colors ${
                            p.cierre?.movimientos_completo 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                          }`}
                          title={p.cierre?.movimientos_completo ? 'Marcar como incompleto' : 'Marcar como completo'}
                        >
                          {p.cierre?.movimientos_completo ? <CheckCircle size={18} /> : <Clock size={18} />}
                        </button>
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleMarcarCierre(p.periodo, 'padron', !p.cierre?.padron_completo)}
                          className={`p-2 rounded-full transition-colors ${
                            p.cierre?.padron_completo 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                          }`}
                          title={p.cierre?.padron_completo ? 'Marcar como incompleto' : 'Marcar como completo'}
                        >
                          {p.cierre?.padron_completo ? <CheckCircle size={18} /> : <Clock size={18} />}
                        </button>
                      </TableCell>
                      <TableCell className="text-center">
                        {p.reportes_generados > 0 ? (
                          <Badge className="bg-blue-100 text-blue-800">{p.reportes_generados}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.ultimo_reporte && !p.presentado && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarcarPresentado(p.ultimo_reporte.id)}
                            className="text-green-600 text-xs"
                          >
                            <Check size={12} className="mr-1" />
                            Presentado
                          </Button>
                        )}
                        {p.presentado && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle size={10} className="mr-1" />
                            OK
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            {/* Leyenda */}
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-xs text-slate-600">
              <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-600" /> Presentado</span>
              <span className="flex items-center gap-1"><FileText size={12} className="text-blue-600" /> Generado</span>
              <span className="flex items-center gap-1"><Check size={12} className="text-emerald-600" /> Datos listos</span>
              <span className="flex items-center gap-1"><AlertTriangle size={12} className="text-amber-600" /> Por vencer</span>
              <span className="flex items-center gap-1"><XCircle size={12} className="text-red-600" /> Vencido</span>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
